import { tracked } from '@glimmer/tracking';
import { CampaignType } from 'cio/models/campaign';
import { MIN_TRIALS } from 'cio/utils/split-tests';
import { MetricDataPoint, Periods, SplitMetricMeasure } from '../common';
import { calculateMeasure, MeasureType, findDenominator } from '../measure';
import { chanceToBeatControl } from 'cio/helpers/cio-math';
import { TimeRange } from 'cio/utils/time';

export interface DashboardTestResult {
  newsletters: DashboardTestNewsletter[];
  splitActions: DashboardTestSplitAction[];
}

export type DashboardTestSplitAction = {
  id: string;
  campaignId: string;
  campaignName: string;
  campaignType: CampaignType;
  name: string;
  actionState: string;
  splitState: string;
  started: number;
  actionIds: string[];
  splitMetrics: DashboardTestSplitMetric[];
};

export type DashboardTestNewsletter = {
  id: string;
  name: string;
  sentAt: number;
  variationCount: number;
  automaticWinner: boolean;
  sendPercentage: number;
  sentToRemainingAt: number;
  testDuration: number;
  testMetric: string;
};

export type UnderscoredMeasure = '_sent' | '_delivered' | '_opened' | '_clicked' | '_converted';

export const UNDERSCORED_MEASURES: Record<SplitMetricMeasure, UnderscoredMeasure> = {
  sent: '_sent',
  delivered: '_delivered',
  opened: '_opened',
  clicked: '_clicked',
  converted: '_converted',
};

function calculateCTBO(
  original: DashboardTestSplitMetric,
  variation: DashboardTestSplitMetric,
  measure: SplitMetricMeasure,
) {
  let originalMeasure = original[measure];
  let variationMeasure = variation[measure];

  if (!originalMeasure || !variationMeasure) {
    return null;
  }

  let originalMeasureTotal = originalMeasure.rawTotal;
  let variationMeasureTotal = variationMeasure.rawTotal;

  let denominator = findDenominator(measure, MeasureType.DeliveryMetrics, 'email_action');

  if (!denominator) {
    return null;
  }

  let originalDenominatorTotal = original[denominator as SplitMetricMeasure]?.rawTotal ?? 0;
  let variationDenominatorTotal = variation[denominator as SplitMetricMeasure]?.rawTotal ?? 0;

  let chance = chanceToBeatControl(
    originalMeasureTotal,
    variationMeasureTotal,
    originalDenominatorTotal,
    variationDenominatorTotal,
  );

  if (isNaN(chance)) {
    return null;
  }

  return chance;
}

export class DashboardTestSplitMetric {
  id: string;
  actionId: string;
  campaignId: string;
  templateId: string;

  actionType = 'email_action';
  canConvert = true;

  period: Periods;
  range: TimeRange;

  @tracked _sent: MetricDataPoint[];
  @tracked _delivered: MetricDataPoint[];
  @tracked _opened: MetricDataPoint[];
  @tracked _clicked: MetricDataPoint[];
  @tracked _converted: MetricDataPoint[];

  get rawMetrics() {
    return {
      sent: this._sent,
      delivered: this._delivered,
      opened: this._opened,
      clicked: this._clicked,
      converted: this._converted,
    };
  }

  get groupedMetrics() {
    let startTimestamp = (this.range.start?.unix() ?? 0) * 1000;
    let endTimestamp = (this.range.end?.unix() ?? 0) * 1000;

    let metrics: Record<any, MetricDataPoint[]> = {};

    ['sent', 'delivered', 'opened', 'clicked', 'converted'].forEach((measure: SplitMetricMeasure) => {
      metrics[measure] = [...this[UNDERSCORED_MEASURES[measure]]].filter(
        (p) => p.x >= startTimestamp && p.x <= endTimestamp,
      );
    });

    return metrics;
  }

  get sent() {
    return calculateMeasure(
      'sent',
      MeasureType.DeliveryMetrics,
      [this],
      this.rawMetrics as any,
      this.groupedMetrics as any,
    );
  }
  get delivered() {
    return calculateMeasure(
      'delivered',
      MeasureType.DeliveryMetrics,
      [this],
      this.rawMetrics as any,
      this.groupedMetrics as any,
    );
  }
  get opened() {
    return calculateMeasure(
      'opened',
      MeasureType.DeliveryMetrics,
      [this],
      this.rawMetrics as any,
      this.groupedMetrics as any,
    );
  }
  get clicked() {
    return calculateMeasure(
      'clicked',
      MeasureType.DeliveryMetrics,
      [this],
      this.rawMetrics as any,
      this.groupedMetrics as any,
    );
  }
  get converted() {
    return calculateMeasure(
      'converted',
      MeasureType.DeliveryMetrics,
      [this],
      this.rawMetrics as any,
      this.groupedMetrics as any,
    );
  }

  hasEnoughDataForCTBO = false;
  openedCTBO: number | null = null;
  clickedCTBO: number | null = null;
  convertedCTBO: number | null = null;

  constructor({
    id,
    action_id,
    campaign_id,
    template_id,
    period,
    range,
    data,
  }: {
    id: string;
    action_id: string;
    campaign_id: string;
    template_id: string;
    period: Periods;
    range: TimeRange;
    data: Record<SplitMetricMeasure, MetricDataPoint[]>;
  }) {
    this.id = id;
    this.actionId = action_id;
    this.campaignId = campaign_id;
    this.templateId = template_id;

    this.period = period;
    this.range = range;

    this._sent = data.sent;
    this._delivered = data.delivered;
    this._opened = data.opened;
    this._clicked = data.clicked;
    this._converted = data.converted;
  }

  processCTBO(original: DashboardTestSplitMetric) {
    // we use "opened" to find a denominator, because the denominator for
    // "clicked" and "converted" will be the same
    let denominator = findDenominator('opened', MeasureType.DeliveryMetrics, 'email_action') as SplitMetricMeasure;
    this.hasEnoughDataForCTBO = (this[denominator]?.rawTotal ?? 0) >= MIN_TRIALS;

    if (!this.hasEnoughDataForCTBO) {
      return;
    }

    this.openedCTBO = calculateCTBO(original, this, 'opened')!;
    this.clickedCTBO = calculateCTBO(original, this, 'clicked')!;
    this.convertedCTBO = calculateCTBO(original, this, 'converted')!;
  }
}
